import React, { FormEvent, ReactElement, ReactNode, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "../styles/CheckoutForm.module.scss";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faCheck, faX } from "@fortawesome/free-solid-svg-icons";
// import { faCheck } from "@fortawesome/free-solid-svg-icons/";
import Log from "../utils/Log";
import classnames from "classnames";
import { openNewWindow } from "../utils/helperUtils";
import isLoggingInFromClient from "../selectors/isLoggingInFromClient";
import GenericInput from "../containers/helpers/genericInput";
import { faUser, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { setAccountlessPurchaseEmail } from "../actions/auth";
// import authReducer from "../reducers/authReducer";
import currentUserInfo from "../selectors/currentUserInfo";
import { StripeCardElement, PaymentMethod, TokenResult } from "@stripe/stripe-js";
// import { faUser } from "@fortawesome/free-solid-svg-icons";
import countryList from "../constants/country_list";
import GenericDropdown from "../containers/helpers/genericDropdown";
import { getItems } from "../selectors/selectCart";
import { getPlanProvider } from "../selectors/currentAccountSelector";
import { PlanInterval, PlanType } from "../reducers/models";

export type FormFields = {
  name: string
  country: string
  email: string
}
 
//Form that uses Stripe UI Elements - used on /purchase and /account/billing
const CheckoutForm = (props:{
  handleSubmitCallback:(e: FormEvent, paymentMethod:PaymentMethod,result:TokenResult,name:string)=>void,
  // handleSubmitCallback:()=>void,
  email:string,
  successMessage?:string,
  formStyles:{
    title: string,
    buttonCTA: string,
    color?:string,
    alignItems?: string,
    backgroundColor?: string,
    border?: boolean,
    darkMode?: boolean,
    paymentFormBackground: string,
    height: string,
    width: string,
    padding: string,
  },
  successHeaderMessage:string,
  loadingMessage:string,
//  isMonthly:boolean,
  planType: PlanType,
  children?: ReactNode|ReactNode[],
}):ReactElement => {
  const clientLoginState = useSelector(isLoggingInFromClient);
  const [loadingState, setLoadingState] = useState(false);
  const [paymentSuccessful, setPaymentSuccess] = useState(false);
  const [submitDisabledState, setSubmitDisabledState] = useState(false);
  const [recurrChecked, setRecurrChecked] = useState(false);
  const [buyNowIsPristine, setBuyNowIsPristine] = useState(true);

  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const userInfo = useSelector(currentUserInfo);
  const userPlanProvider = useSelector(getPlanProvider);
  const selectedCartItems = useSelector(getItems)[0];
  const item = selectedCartItems ? selectedCartItems.item : undefined;
  const msg = item ? "" : "Nothing in cart";
  const [generalErrorText, setGeneralError] = useState(msg);
  const enforceStripe = userPlanProvider && item && (userPlanProvider as Record<string, any>)[item.productType.toLowerCase()] === "stripe";

  //console.log("item", item);
  //console.log("userPlanProvider", userPlanProvider);
  
  

  const countryArray = countryList.names;
  const countryCodesArray = countryList.withCodes;

  useEffect(() => {
    recurrChecked ? setSubmitDisabledState(false) : setSubmitDisabledState(true);
  }, [recurrChecked]);

  useEffect(() => {
    if (userInfo?.uuid) {
      updateField({...fields, email: userInfo.email});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  const {
    handleSubmitCallback,
    email,
    successMessage,
    formStyles,
    successHeaderMessage,
    loadingMessage,
    //isMonthly,
    //planType,
  } = props;

  const formFields = {
    name: "",
    country: "",
    email: "",
  };

  const [fields, updateField] = useState<FormFields>({...formFields});

  const [errorText, updateErrorMessage] = useState<FormFields>({...formFields});

  function validateForm():boolean {
    const values = Object.values(fields);
    const fieldsAreValid = values.every((value) => value !== "") && recurrChecked;

    if (!fieldsAreValid) {
      const currentErrorObject = {...errorText} as FormFields;
      for (const objKey in fields) {
        const inputName = objKey as keyof FormFields
        if (!validateText(fields[inputName])) {
          currentErrorObject[inputName] = getErrorMessage(inputName);
        }
      }

      updateErrorMessage({...currentErrorObject});
    }

    return fieldsAreValid;
  }

  function validateText(value?:string):boolean {
    return value !== undefined && value !== ""
  }

  async function handleSubmit(event:FormEvent) {
    setBuyNowIsPristine(false);
    event.preventDefault();
    setSubmitDisabledState(true);
    setGeneralError("");

    if (!validateForm()) {
      Log.warn("Form entry error!", "Checkout");

      // Something in our regular form elements is not filled out
      handlePaymentSubmissionError(
        "Some billing details are missing, please check your billing details and try again."
      );
      return;
    }

    if (!stripe || !elements) {
      Log.warn("Stripe load error!", "Checkout");
      // Stripe not loaded, trigger error message
      handlePaymentSubmissionError(
        "Payment processor is unavailable, your card has not been charged. Please contact support."
      );
      return;
    }

    if (!userInfo?.uuid) {
      dispatch(setAccountlessPurchaseEmail(fields.email))
    }

    const card = elements.getElement(CardElement) as StripeCardElement;
    const result = await stripe.createToken(card, {
      name: fields.name,
      address_country: fields.country,
    });

    // Payment Method version of stripe requires country codes
    let countryCode = countryCodesArray.find((item) => {
      return item.name === fields.country;
    })?.code;
    

    const { paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: card,
      billing_details: {
        email: email || fields.email,
        address: {
          country: countryCode
        },
      },
    });
    

    setLoadingState(true);

    if (result?.error || (paymentMethod as any)?.error) {
      Log.error(
        result?.error?.message,
        "Checkout Component: error caught on handlePayment"
      );
      // Display stripe error message
      // Should be an input error somewhere in the stripe card element
      handlePaymentSubmissionError(
        "Unable to process your purchase, please check your credit card numbers and try again."
      );
    } else {
      try {
        await handleSubmitCallback(event,
          paymentMethod as PaymentMethod,
          result,
          fields.name
        );
        handlePaymentSuccess();
      } catch (error:any) {
        Log.error("error caught in checkout component", error);
        handlePaymentSubmissionError(error.message);
      }
    }
  }

  function handlePaymentSubmissionError(error:any) {
    setSubmitDisabledState(false);
    setGeneralError(error);
    setLoadingState(false);
  }

  function handlePaymentSuccess() {
    setPaymentSuccess(true);
    setLoadingState(false);
  }

  function handleFieldChange(inputName:string, value:string) {
    if (!userInfo?.uuid && inputName === "email") {
      dispatch(setAccountlessPurchaseEmail(value))
    }
    if (validateText(value)) {
      updateErrorMessage({...errorText, [inputName]: ""});
    } else {
      updateErrorMessage({...errorText, [inputName]: getErrorMessage(inputName)});
    }

    updateField({...fields, [inputName]:value});
  }

  function handleLinkClick(url:string) {
    if (clientLoginState) {
      openNewWindow(url, false);
    } else {
      window.open(url);
    }
  }

  const CARD_ELEMENT_OPTIONS = {
    classes: {
      base: formStyles.darkMode
        ? styles.darkElementContainer
        : styles.elementContainer,
      focus: styles.elementFocused,
    },
    style: {
      base: {
        fontSize: "16px",
        "::placeholder": {
          color: "#676767",
          fontSize: "16px",
          backgroundColor: formStyles.darkMode ? "#222222" : "#fff",
        },
        color: formStyles.darkMode ? "#fff" : "#676767",
        backgroundColor: formStyles.darkMode ? "#222222" : "#fff",
      },
    },
  };

  const paymentFormStyle = {
    background: formStyles.paymentFormBackground,
    height: formStyles.height,
    width: formStyles.width,
    padding: formStyles.padding,
  };

  function getErrorMessage(inputName:string|undefined) {
    let inputTypeText = "Please enter a valid ";

    switch (inputName) {
      // case "city":
      // case "name":
      // case "country":
      case "email":
        inputTypeText += inputName + ".";
        break;
      // case "streetAddress":
      //   inputTypeText += "street address.";
      //   break;
      default:
        inputTypeText = "";
    }

    return inputTypeText;
  }

  const inputStyles = {
    container: styles.paymentInputWrapper,
    label: formStyles.darkMode
      ? classnames(styles.paymentInputLabel, styles.darkMode)
      : styles.paymentInputLabel,
    input: formStyles.darkMode
      ? classnames(styles.paymentInput, styles.darkMode)
      : styles.paymentInput,
    errorMessage: styles.paymentErrorText,
    icon: styles.inputIcon,
  };

  const paymentContainerClass = classnames(
    styles.payment,
    formStyles.border && styles.border
  );
  if (paymentSuccessful && generalErrorText === "") {
    return (
      <div className={paymentContainerClass} style={{ ...paymentFormStyle }}>
        <div className={styles.checkoutForm}>
          <div className={styles.loadingIconContainer}>
            <h2
              style={{ color: formStyles.color ? formStyles.color : "black" }}
            >
              {successHeaderMessage}
            </h2>
            <FontAwesomeIcon className={styles.checkIcon} icon={faCheck} />
            <p className={styles.loadingIconContainerP} style={{ color: formStyles.color ? formStyles.color : "black" }}>
              {successMessage}
            </p>
          </div>
        </div>
      </div>
    );
  }
  if (loadingState) {
    return (
      <div className={paymentContainerClass} style={{ ...paymentFormStyle }}>
        <div className={styles.checkoutForm}>
          <div className={styles.loadingIconContainer}>
            <h2
              style={{ color: formStyles.color ? formStyles.color : "black" }}
            >
              {loadingMessage}
            </h2>
            <FontAwesomeIcon
              className={styles.loadingIcon}
              icon={faSpinner}
              spin
            />
          </div>
        </div>
      </div>
    );
  }
  if( generalErrorText !== ""){
    return ( 
    <div className={paymentContainerClass} style={{ ...paymentFormStyle }}>
        <div className={styles.checkoutForm}>
          <div className={styles.loadingIconContainer}>
            <h1
              className={styles.failedHeader}
              style={{ color: formStyles.color ? formStyles.color : "black" }}
            >
              Payment Failed
            </h1>
               <FontAwesomeIcon
                className={styles.failedIcon}
                icon={faX}
                size="xs"
                color="red"
              />
             <p className={styles.loadingIconContainerP} style={{ color: formStyles.color ? formStyles.color : "black" }}>
              {generalErrorText}
            </p>
          </div>
        </div>
      </div>)
  }

  function renderFormArea() {
    if(!item) {
      return null;
    }
    
    const yearOrMonthText = item.planInterval === PlanInterval.Monthly ? "monthly" : item.planInterval === PlanInterval.Weekly ? "weekly" : "yearly";
    const annualOrMonthlyText = item.planInterval === PlanInterval.Monthly ? "monthly" : item.planInterval === PlanInterval.Weekly ? "weekly" : "annual";
    return (
      <>
        <h3 className={styles.headerStyle}>{formStyles.title}</h3>
        <div className={styles.generalErrors}>{generalErrorText}</div>
        <div className={styles.splitRow}>
          <GenericInput
            type={"email"}
            title={"Email:"}
            placeholder={"Email"}
            inputName={"email"}
            inputValue={userInfo?.uuid ? userInfo?.email : fields.email}
            changeCallback={handleFieldChange}
            errorMessage={errorText.email}
            styles={inputStyles}
            icon={faEnvelope}
            disabled={userInfo?.uuid ? true : false}
          />

          <CardElement
            className={styles.cardInput}
            options={CARD_ELEMENT_OPTIONS}
          />

          <GenericInput
            type={"text"}
            title={"Cardholder name:"}
            placeholder={"Full name on card"}
            inputName={"name" as keyof FormFields}
            inputValue={fields.name}
            changeCallback={handleFieldChange}
            errorMessage={errorText.name}
            styles={inputStyles}
            icon={faUser}
          />

          <GenericDropdown
            title={"Country: "}
            inputName={"country"}
            inputValue={fields.country}
            listItemArray={countryArray}
            changeCallback={handleFieldChange}
            errorMessage={errorText.country}
            errorMessageStyle={styles.dropdownErrorMessage}
            styles={{
              width: "100%",
              container: styles.dropdownWrapper,
              backgroundColor: formStyles.darkMode ? "#222222" : "#fff",
              color: formStyles.darkMode ? "#fff" : "#000",
            }}
            className={formStyles.darkMode ? styles.darkSelect : styles.select}
          />
        </div>
        <div className={
            formStyles.darkMode ? styles.darkModeLegalText : styles.legalText
          }>
            By selecting "Subscribe" I authorize the charge for the {yearOrMonthText} subscription payments starting today as indicated above. I understand I can cancel my {annualOrMonthlyText} subscription anytime, prior to its next renewal and that my purchases are non-refundable.
          </div>
          <div className={styles.mB}></div>
          <p className={styles.termsError}>{!recurrChecked && !buyNowIsPristine ? 'Agree to the terms first.' : ''}</p>
        <div className={styles.checkboxWrapper}>
      <input
        type="checkbox"
        id="auto-renew"
        checked={recurrChecked}
        onChange={() => setRecurrChecked(!recurrChecked)}
      /><label className={styles.checkbox} htmlFor="auto-renew">
      <span className={
            formStyles.darkMode ? styles.darkModeLegalText : styles.legalText
          }>
            I have read and understand the terms described above, as well as our{" "}
          <button
            type="button"
            onClick={() => handleLinkClick("https://www.buildbox.com/terms/")}
          >
            Terms and Conditions
          </button>
          ,
          <button
            type="button"
            onClick={() =>
              handleLinkClick("https://www.buildbox.com/privacy-policy/")
            }
          >
            Privacy Policy
          </button>
          ,{" "}
          <button
            type="button"
            onClick={() =>
              handleLinkClick(
                "https://www.buildbox.com/asset-store-terms-of-service/"
              )
            }
          >
            Assets Store Terms of Service
          </button>{" "}
          and{" "}
          <button
            type="button"
            onClick={() => handleLinkClick("https://www.buildbox.com/eula/")}
          >
            End User License Agreement
          </button>
    </span>
    </label>
     </div>
        <div>
          <button
            disabled={submitDisabledState}
            className={
              formStyles.darkMode
                ? styles.darkCheckoutButton
                : styles.checkoutButton
            }
          >
            {formStyles.buttonCTA}
          </button>
        </div>
        {!enforceStripe && props.children}
      </>
    );
  }

  return (
    <div
      className={paymentContainerClass}
      style={{
        alignItems: formStyles.alignItems,
        backgroundColor: formStyles.backgroundColor,
      }}
    >
      <form
        className={styles.checkoutForm}
        style={{ padding: formStyles.padding }}
        onSubmit={handleSubmit}
      >
        {renderFormArea()}
      </form>
    </div>
  );
};

export default CheckoutForm;
